import type { Entity } from '@backstage/catalog-model';
import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/core-plugin-api';

/**
 * Escape hatch for the few entities whose Argo CD Applications are not named
 * `<entity name>-<cluster>`; the value is a comma separated list of app names.
 */
export const ARGOCD_APP_NAMES_ANNOTATION = 'argocd/app-names';

/**
 * Overrides the service name used to derive `<service>-<cluster>` Application
 * names, for entities whose Applications share a prefix that is not the entity
 * name - e.g. a Dagster user-deployment named `dagster-user-deployment-r2-*`
 * behind the entity `r2-dagster`. Unlike argocd/app-names this keeps the
 * discovery path, so the per-cluster column stays populated and newly
 * registered clusters are picked up without editing the annotation.
 */
export const ARGOCD_SERVICE_NAME_ANNOTATION = 'argocd/service-name';

/** One Argo CD Application, as flattened by the argocd-discovery backend. */
export type Deployment = {
  name: string;
  instance: string;
  cluster?: string;
  namespace?: string;
  health: string;
  sync: string;
  image?: string;
  syncedAt?: string;
  sourcePath?: string;
  url: string;
};

/**
 * Query for an entity's deployments. Entities are looked up by name, which is
 * what makes a newly catalogued service show its deployments without any
 * annotation or config change. The lookup name can be overridden with
 * argocd/service-name, or replaced with an explicit argocd/app-names list.
 */
export function deploymentQuery(entity: Entity): string {
  const annotations = entity.metadata.annotations ?? {};
  const names = annotations[ARGOCD_APP_NAMES_ANNOTATION];
  if (names) {
    return new URLSearchParams({ names }).toString();
  }

  const service =
    annotations[ARGOCD_SERVICE_NAME_ANNOTATION] ?? entity.metadata.name;
  return new URLSearchParams({ service }).toString();
}

export class ArgocdDiscoveryApi {
  constructor(
    private readonly deps: { discoveryApi: DiscoveryApi; fetchApi: FetchApi },
  ) {}

  async deployments(query: string): Promise<Deployment[]> {
    const baseUrl = await this.deps.discoveryApi.getBaseUrl('argocd-discovery');
    const response = await this.deps.fetchApi.fetch(
      `${baseUrl}/applications?${query}`,
    );

    if (!response.ok) {
      throw new Error(
        `Argo CD deployment lookup failed: ${response.status} ${response.statusText}`,
      );
    }
    const { items } = (await response.json()) as { items: Deployment[] };
    return items;
  }
}

export const argocdDiscoveryApiRef = createApiRef<ArgocdDiscoveryApi>({
  id: 'plugin.argocd-discovery.service',
});
